import React, { ReactNode } from 'react';
import {TFunction} from 'i18next';

import Dialog from '~src/shared/dialogs/dialog/Dialog';
import PrimaryButtonLoading from '~src/shared/buttons/loadingButtons/PrimaryButtonLoading';
import SecondaryButton from '~src/shared/buttons/basicButtons/SecondaryButton.sc';
import CustomFormInput from '~src/shared/form/inputs/FormInput';

import {
  ButtonContainer,
  ButtonMargin,
  ActionContent,
  ActionErrorMsg,
  ActionHeader,
  IconContainer,
  ModalContainer,
  InputWrapper,
  ActionInputLabel,
} from './actionConfirmDialog.sc';

interface DefaultProps {
  show: boolean;
  close: boolean;
  footer: string | null;
  children: ReactNode;
  actionTitleKey: string | null;
  titleBarKey: string;
  actionContentKey: string | null;
  loadingAction: boolean;
  showDeletion: boolean;
  actionError: string | null;
  confirmInpLabel: string;
  btnCancelKey: string;
  btnOkKey: string;
}

interface Props extends DefaultProps {
  onHide: () => void;
  actionCall: () => void;
  t: TFunction;
  confirmInpTitle?: string;
  confirmActionInput?: string;
}

interface State {
  delInpValue: string;
}

class ActionConfirmDialog extends React.Component<Props, State> {
  static defaultProps: DefaultProps = {
    show: false,
    close: false,
    footer: null,
    children: null,
    actionTitleKey: null,
    titleBarKey: 'x.warning',
    actionContentKey: null,
    loadingAction: false,
    showDeletion: false,
    actionError: null,
    confirmInpLabel: '',
    btnCancelKey: 'x.button.cancel',
    btnOkKey: 'x.button.delete',
  };

  state = {
    delInpValue: '',
  };

  onInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    this.setState({ delInpValue: e.target.value });
  };

  onDialogClose = () => {
    this.props.onHide();
    this.setState({ delInpValue: '' });
  };

  isOkDisabled = (): boolean => {
    const { confirmActionInput } = this.props;
    if (confirmActionInput) {
      return confirmActionInput !== this.state.delInpValue.toLowerCase();
    }
    return false;
  };

  render() {
    const {
      t,
      actionTitleKey,
      actionContentKey,
      actionCall,
      loadingAction,
      actionError,
      titleBarKey,
      confirmInpTitle,
      confirmInpLabel,
    } = this.props;
    return (
      <Dialog
        show={this.props.show}
        close={this.props.close}
        title={t(titleBarKey)}
        titleKey={titleBarKey}
        onHide={this.onDialogClose}
      >
        <ModalContainer>
          <IconContainer width="60px" name="iconDanger" />
          {actionTitleKey && (
            <ActionHeader data-translation={actionTitleKey}>{t(actionTitleKey)}</ActionHeader>
          )}
          {actionContentKey && (
            <ActionContent data-translation={actionContentKey}>{t(actionContentKey)}</ActionContent>
          )}
          {this.props.confirmActionInput && (
            <>
              {confirmInpTitle && (
                <ActionInputLabel data-translation={confirmInpTitle}>{t(confirmInpTitle)}</ActionInputLabel>
              )}
              <InputWrapper data-translation={confirmInpLabel}>
                <CustomFormInput
                  label={t(confirmInpLabel)}
                  onChange={this.onInputChange}
                  value={this.state.delInpValue}
                  labelKey={confirmInpLabel}
                />
              </InputWrapper>
            </>
          )}
          <ButtonContainer>
            <ButtonMargin>
              <SecondaryButton
                onClick={this.onDialogClose}
                data-translation={this.props.btnCancelKey}
                disabled={loadingAction}
              >
                {t(this.props.btnCancelKey)}
              </SecondaryButton>
            </ButtonMargin>
            {this.props.showDeletion && (
              <PrimaryButtonLoading
                $buttonColor="reddish"
                onClick={actionCall}
                isLoading={loadingAction}
                disabled={this.isOkDisabled()}
              >
                <span data-translation={this.props.btnOkKey}>{t(this.props.btnOkKey)}</span>
              </PrimaryButtonLoading>
            )}
          </ButtonContainer>
          {actionError && <ActionErrorMsg data-translation={actionError}>{t(actionError)}</ActionErrorMsg>}
        </ModalContainer>
      </Dialog>
    );
  }
}

export default ActionConfirmDialog;
